import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, useDeferredValue, lazy, Suspense } from 'react';
import { useTransactions } from './hooks/useTransactions';
import { fetchMarketData, COIN_MAP } from './utils/coingecko';
import { resolveCoins } from './utils/coinResolver';
import { safeLocalSet } from './utils/lsCache';
import { enrichMissingPrices } from './utils/swapPriceEnricher';
import { resolveSplMarket } from './utils/splMarket';
import { isFiatCoin } from './utils/transactionTypes';
import { matchTransfers, normalizeTransactions, persistTransferLinks } from './utils/transferMatcher';
import { enrichTransactions } from './utils/blockchainEnricher';
import { classifyOwnWalletTransfers } from './utils/ownWalletClassifier';
import { listWallets, ensureOwnWallets } from './services/walletRegistry';
import { autoImportOwnTransferCandidates } from './utils/autoImportCandidates';
import { F } from './styles/theme';
import { useTheme } from './context/ThemeContext';
import { useAuth } from './context/AuthContext';
import { useProfile } from './context/ProfileContext';
import { useT } from './i18n/useT';
import { useUserPlan } from './context/UserPlanContext';
import { MarketDataContext } from './context/MarketDataContext';
import { getYearUsage, checkBatchAgainstLimit, getTxYear, getCurrentTaxYear } from './utils/txLimits';
import TopBar from './components/TopBar';
import BottomNav from './components/BottomNav';
import PullToRefresh from './components/red/PullToRefresh';
import Toast from './components/Toast';
import LimitGateModal from './components/LimitGateModal';
import SupportModal from './components/SupportModal';
import PricingScreen from './screens/PricingScreen';

import Dashboard from './screens/Dashboard';
import TransactionWizard, { TransactionEditScreen } from './screens/TransactionWizard';
import WizardSheet from './components/wizard/WizardSheet';
import HistorieScreen from './screens/TaxScreen';
import DataCheckFixScreen from './screens/DataCheckFixScreen';
import SettingsScreen from './screens/SettingsScreen';
import SolanaWalletConnect from './components/SolanaWalletConnect';
import ChainWalletConnect from './components/ChainWalletConnect';
import WalletRegistry from './components/WalletRegistry';
import AssetDetailScreen from './screens/AssetDetailScreen';
import WalletScreen from './screens/WalletScreen';
import ProfileScreen from './screens/ProfileScreen';
import AlertsScreen from './screens/AlertsScreen';
import { scheduleAlertSnapshot } from './services/alertSnapshot';
import PrivacyNotice from './components/PrivacyNotice';
import LoginScreen from './screens/LoginScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import ConnectFirstSource from './screens/ConnectFirstSource';
import TourOverlay from './components/TourOverlay';
import MarketsScreen from './screens/MarketsScreen';
import DesktopShell from './layouts/DesktopShell';
import { useBreakpoint } from './hooks/useBreakpoint';
import { useVersionCheck } from './hooks/useVersionCheck';
import UpdateBanner from './components/UpdateBanner';

// Ionic-Shell ist jetzt der MOBILE-STANDARD (native Navigation + swipe-back).
// LAZY, damit Ionics globale CSS sauber im eigenen Chunk bleibt. Notausgang auf
// den alten Shell: ?oldnav=1.
const IonicShell = lazy(() => import('./IonicShell'));
const FORCE_OLD_NAV = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('oldnav');

// Stiller Lade-Hintergrund während authLoading/txsLoading. Bewusst ohne Logo:
// die Eröffnung übernimmt der animierte RedSplash (s. index.js); ein zweites
// statisches Logo beim Reload wirkte doppelt. Neutraler Theme-BG verhindert
// weißen Blitz.
function SplashScreen() {
  const { T } = useTheme();
  return <div style={{ minHeight: '100vh', background: T.bg }} />;
}

// Hält den (oft schweren) Screen-Wechsel von der dringenden Render-Bahn fern.
// React 19 rendert `children` deferred → die Nav-Pille (dringendes Update im
// Parent) gleitet sofort los, während der Ziel-Screen erst danach (time-sliced)
// mountet. Behebt die Asymmetrie „nach oben zu Dashboard/Transaktionen ruckelt"
// — dort lag der 245-ms-Mount sonst direkt vor dem Start der Thumb-Animation.
// Eigene Komponente, damit der Hook unkonditional läuft (App hat Early-Returns).
function DeferredScreen({ children }) {
  return useDeferredValue(children);
}

// Wisch-Navigation (recherchiert, robust): Rechts-Wisch. Dashboard → öffnet
// Märkte (liegen konzeptionell links); Sub-View (Detail/Märkte/Menü) → schließt.
// Prinzipien (best practice): früher Richtungs-Lock (|dx| vs |dy|), Auslösen per
// Distanz (≥64px) ODER Velocity-Flick (≥0.4px/ms, ab 24px), horizontal-genug.
// Von ÜBERALL auslösbar (nicht nur Kante) — aber ein Guard überlässt horizontal
// scrollbaren Kindern (Top-Mover-Strip) den Touch. Kein touch-action:pan-y am
// Container, damit native Horizontal-Scroller weiter funktionieren.
function SwipeNav({ onSwipeRight, style, children }) {
  const st = useRef(null);
  const onStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) { st.current = null; return; }
    // Natürliche Gesten ausnehmen (Charts/Graphen): Elemente mit [data-noswipe]
    // behalten ihre eigene Horizontal-Geste (z.B. Chart-Scrubbing in der Detail-
    // ansicht) → kein Seitenwechsel.
    if (e.target && e.target.closest && e.target.closest('[data-noswipe]')) { st.current = null; return; }
    // Start in einem horizontal scrollbaren Element? → nativem Scroll überlassen.
    let el = e.target;
    while (el && el !== e.currentTarget) {
      if (el.scrollWidth > el.clientWidth + 4) {
        const ox = (typeof getComputedStyle === 'function') ? getComputedStyle(el).overflowX : '';
        if (ox === 'auto' || ox === 'scroll') { st.current = null; return; }
      }
      el = el.parentElement;
    }
    st.current = { x0: t.clientX, y0: t.clientY, t0: e.timeStamp, lock: null };
  };
  const onMove = (e) => {
    const s = st.current;
    if (!s || s.lock) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - s.x0), dy = Math.abs(t.clientY - s.y0);
    if (dx > 10 || dy > 10) s.lock = dx > dy ? 'h' : 'v'; // Richtung früh festnageln
  };
  const onEnd = (e) => {
    const s = st.current; st.current = null;
    if (!s || s.lock !== 'h') return; // vertikaler Scroll → ignorieren
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x0, dy = t.clientY - s.y0;
    const dt = Math.max(1, e.timeStamp - s.t0);
    const vx = dx / dt; // px pro ms
    const horizontalEnough = Math.abs(dy) < Math.abs(dx) * 0.7;
    const far = dx >= 64;                 // klare Distanz
    const flick = vx >= 0.4 && dx >= 24;  // schneller Flick reicht mit weniger Weg
    if (dx > 0 && horizontalEnough && (far || flick)) onSwipeRight && onSwipeRight();
  };
  return (
    <div onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} style={style}>
      {children}
    </div>
  );
}

export default function App() {
  const { T } = useTheme();
  const { t } = useT();
  const { user, loading: authLoading } = useAuth();
  const { onboardingComplete, tutorialSeen, setTutorialSeen, firstSourceSeen, setFirstSourceSeen } = useProfile();
  const { txs, loading: txsLoading, addTx, updateTx, patchTx, deleteTx, deleteBulkTxs, addBatch, deleteAllTxs } = useTransactions();
  const { tier, txYearLimit } = useUserPlan();
  const { isWide } = useBreakpoint();
  // Erkennt, wenn ein neueres App-Bundle deployt wurde, während dieser Client
  // (z.B. offene Handy-PWA) noch ein altes ausführt → verhindert, dass Mobile mit
  // veralteter Rechenlogik andere Zahlen zeigt als der frisch geladene Desktop.
  const updateAvailable = useVersionCheck();
  const [tab, setTab] = useState('dashboard');
  const [wizardMode, setWizardMode] = useState(null);
  const [editingTx, setEditingTx] = useState(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [limitGate, setLimitGate] = useState(null); // { mode, usage }
  const [marketData, setMarketData] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('red_market_cache') || 'null');
      if (cached && Date.now() - cached.ts < 24 * 60 * 60 * 1000) return cached.data;
    } catch {}
    return {};
  });
  const [toast, setToast] = useState('');
  const [, setMarketLoading] = useState(false);
  // Spiegel von marketData für loadMarket (synchroner Lookup „hat dieser Coin
  // schon einen Preis?" ohne auf den nächsten Render zu warten).
  const marketDataRef = useRef(marketData);
  useEffect(() => { marketDataRef.current = marketData; }, [marketData]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [alertPrefill, setAlertPrefill] = useState(null);
  // Wallet-View-State (in App.js gehalten, damit Edit-Roundtrips den Modus erhalten)
  const [walletView, setWalletView] = useState({
    filterUnmatched: false,
    activeTab: 'all',
    filterText: '',
  });
  // Sheet-Progress + Back-Handler: wird vom Wizard via onStepChange aktualisiert
  const [wizardStep, setWizardStep] = useState({ step: 0, total: 1 });
  const [wizardGoBack, setWizardGoBack] = useState(null);
  // viewStack: jeder Eintrag ist ein Snapshot {tab, wizardMode, editingTx, selectedAsset, marketsOpen, walletView}.
  // Wird genutzt, damit "Zurück" den Benutzer wirklich zur vorigen Ansicht bringt — egal woher er kam.
  // Wert exponiert (nicht nur Setter): Desktop entscheidet anhand der Stack-Tiefe, ob Alerts einen
  // Zurück-Pfeil zeigt (von Profil/Dashboard geöffnet = Stack gefüllt) oder nicht (Sidebar-Top-Level = leer).
  const [viewStack, setViewStack] = useState([]);

  const snapshotView = useCallback(() => ({
    tab, wizardMode, editingTx, selectedAsset, marketsOpen, walletView, alertPrefill,
  }), [tab, wizardMode, editingTx, selectedAsset, marketsOpen, walletView, alertPrefill]);

  const applyView = useCallback((v) => {
    setTab(v.tab ?? 'dashboard');
    setWizardMode(v.wizardMode ?? null);
    setEditingTx(v.editingTx ?? null);
    setSelectedAsset(v.selectedAsset ?? null);
    setMarketsOpen(v.marketsOpen ?? false);
    setAlertPrefill(v.alertPrefill ?? null);
    if (v.walletView) setWalletView(v.walletView);
  }, []);

  const pushView = useCallback((partial) => {
    setViewStack(s => {
      const next = [...s, snapshotView()];
      return next.length > 8 ? next.slice(next.length - 8) : next;
    });
    applyView({ tab, wizardMode, editingTx, selectedAsset, marketsOpen, walletView, alertPrefill, ...partial });
  }, [snapshotView, applyView, tab, wizardMode, editingTx, selectedAsset, marketsOpen, walletView, alertPrefill]);

  const popView = useCallback(() => {
    setViewStack(s => {
      if (!s.length) {
        applyView({ tab: 'dashboard' });
        return s;
      }
      const prev = s[s.length - 1];
      applyView(prev);
      return s.slice(0, -1);
    });
  }, [applyView]);

  // Schließt den kompletten "Transaktion hinzufügen"-Flow (choose → csv/api/wallet
  // → Import). NICHT popView verwenden: der Stack enthält bewusst den 'choose'-
  // Snapshot (damit der Zurück-Button dorthin führt), wodurch popView nach einem
  // erfolgreichen Import das Hinzufügen-Menü wieder aufpoppen ließe. Hier räumen
  // wir Stack + alle Sub-View-States ab und bleiben auf dem aktuellen Tab.
  const exitImportFlow = useCallback(() => {
    setViewStack([]);
    setWizardMode(null);
    setEditingTx(null);
    setSelectedAsset(null);
    setMarketsOpen(false);
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2800);
  }, []);

  const loadingRef = useRef(false);
  const txsRef = useRef(txs);
  useEffect(() => { txsRef.current = txs; }, [txs]);

  // Alert-Snapshot für Premium-Alerts (Haltefrist, Portfolio-Wert): der Cron
  // liest Bestände/Lot-Daten aus diesem Snapshot, statt FIFO server-seitig zu
  // duplizieren (Invariant 7: eine FIFO-Engine). Debounced, Best-Effort.
  useEffect(() => {
    if (user?.uid && txs.length) scheduleAlertSnapshot(user.uid, txs);
  }, [user?.uid, txs]);

  const loadMarket = useCallback(async (force = false) => {
    if (loadingRef.current && !force) return;

    // Alle Symbols aus TXs sammeln — auch solche, die nicht in COIN_MAP stehen.
    // Der Resolver übernimmt Long-Tail-Lookup via CoinGecko /search und
    // mutiert COIN_MAP, damit Downstream-Konsumenten den ID-Hit sehen.
    const syms = new Set();
    for (const t of txsRef.current) {
      if (t.typ === 'swap') {
        if (t.coinOut) syms.add(String(t.coinOut).toUpperCase());
        if (t.coinIn)  syms.add(String(t.coinIn).toUpperCase());
      } else if (t.coin) {
        syms.add(String(t.coin).toUpperCase());
      }
    }
    // Tx-eigene coinId-Felder als zusätzlicher Fallback (CSV-Import speichert sie).
    const txIds = txsRef.current.flatMap(t =>
      t.typ === 'swap' ? [t.coinIdOut, t.coinIdIn].filter(Boolean) : [t.coinId].filter(Boolean)
    );

    // Unbekannte Syms async via CG /search auflösen (mutiert COIN_MAP).
    await resolveCoins([...syms]);

    const ids = [...new Set([
      ...[...syms].map(s => COIN_MAP[s]?.id).filter(Boolean),
      ...txIds,
    ])];
    if (!ids.length) return;
    loadingRef.current = true;
    setMarketLoading(true);
    try {
      const data = await fetchMarketData(ids, 'eur', { force });
      // Leeres Resultat ignorieren — sonst überschreibt ein Fehlschlag gute Daten.
      // MERGEN statt ersetzen: ein partielles/abgebrochenes Ergebnis (z.B. nur die
      // gecachten Coins nach einem abgebrochenen Fetch) darf die marketData NIE
      // schrumpfen lassen → sonst stehen plötzlich viele Coins auf 0 €.
      if (data && Object.keys(data).length) {
        setMarketData(prev => {
          const merged = { ...prev, ...data };
          safeLocalSet('red_market_cache', JSON.stringify({ ts: Date.now(), data: merged }));
          return merged;
        });
      }

      // ── SPL-Long-Tail via Jupiter ───────────────────────────────────────
      // Coins, die CoinGecko NICHT kennt (NAKMAK & Co. von Solana), haben jetzt
      // noch keinen Preis/Icon. Jupiter kennt sie — per Symbol (greift auch ohne
      // gespeicherte Mint) oder, falls vorhanden, per Mint (eindeutig). Ergebnis
      // wird wie CoinGecko-Daten in marketData gemerged → Bestand + Icon greifen.
      const haveCgPrice = (S) => {
        const id = COIN_MAP[S]?.id;
        if (!id) return false;
        return Number(data?.[id]?.price) > 0 || Number(marketDataRef.current?.[id]?.price) > 0;
      };
      const mintBySym = new Map();
      const addMint = (sym, mint) => {
        const S = String(sym || '').toUpperCase();
        if (S && mint && !mintBySym.has(S)) mintBySym.set(S, mint);
      };
      for (const t of txsRef.current) {
        if (t.typ === 'swap') { addMint(t.coinOut, t.mintOut); addMint(t.coinIn, t.mintIn); }
        else { addMint(t.coin, t.mint); }
      }
      const splCandidates = [...syms]
        .filter((S) => !isFiatCoin(S) && !haveCgPrice(S))
        .map((S) => ({ sym: S, mint: mintBySym.get(S) || null }));
      if (splCandidates.length) {
        const patch = await resolveSplMarket(splCandidates);
        if (patch && Object.keys(patch).length) {
          setMarketData(prev => {
            const merged = { ...prev, ...patch };
            safeLocalSet('red_market_cache', JSON.stringify({ ts: Date.now(), data: merged }));
            return merged;
          });
        }
      }
    } finally {
      loadingRef.current = false;
      setMarketLoading(false);
    }
  }, []);

  // Pull-to-Refresh (Mobile-Dashboard): erzwingt einen frischen CoinGecko-Load
  // (Portfolio-Preise) und stößt selbst-fetchende Markt-Widgets via Event an.
  const handlePullRefresh = useCallback(async () => {
    try { await loadMarket(true); }
    finally { try { window.dispatchEvent(new Event('red:market-refresh')); } catch {} }
  }, [loadMarket]);

  // Stabile Signatur: alle vorkommenden Symbols — feuert loadMarket auch dann,
  // wenn ein Long-Tail-Coin auftaucht, für den COIN_MAP (noch) keinen Eintrag
  // hat. Der Resolver erledigt das Symbol→ID-Lookup im loadMarket-Body.
  const coinIdSignature = useMemo(() => {
    const syms = new Set();
    txs.forEach(t => {
      if (t.typ === 'swap') {
        if (t.coinOut) syms.add(String(t.coinOut).toUpperCase());
        if (t.coinIn)  syms.add(String(t.coinIn).toUpperCase());
      } else if (t.coin) {
        syms.add(String(t.coin).toUpperCase());
      }
    });
    return [...syms].sort().join(',');
  }, [txs]);

  useEffect(() => {
    if (!coinIdSignature) return;
    // Debounce: rapid Edits/Deletes coalescen zu einem einzigen Fetch
    const debounce = setTimeout(() => { loadMarket(); }, 800);
    // Poll NUR wenn die App/der Tab sichtbar ist — im Hintergrund weiterzupollen
    // verbrennt nur CoinGecko-Credits ohne Nutzen. Beim Zurückkommen 1× auffrischen.
    const tick = () => { if (!document.hidden) loadMarket(); };
    const interval = setInterval(tick, 5 * 60 * 1000);
    const onVis = () => { if (!document.hidden) loadMarket(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearTimeout(debounce); clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [coinIdSignature, loadMarket]);

  // ENTFERNT: Auto-"Dezimal-Korrektur" die menge/1000 rechnete.
  //
  // Diese Heuristik hielt grosse ganzzahlige Mengen mit winzigem Stueckpreis
  // fuer 1000x-Tippfehler und teilte sie durch 1000. Das war fundamental falsch:
  // sub-cent Coins (SHIB, LUNC, BTTC, WIN, BABYDOGE, PEPE, …) haben legitim
  // riesige Mengen bei winzigem Stueckpreis. 8.090.612 SHIB fuer 1.495 €
  // (0,000185 €/Stueck) ist KORREKT — die Heuristik machte daraus 8090,612 SHIB.
  //
  // Ein echter Tippfehler und ein legitim billiger Coin sind per Stueckpreis
  // mathematisch NICHT unterscheidbar — jede Schwelle erzeugt False Positives.
  // Die Korruption lief bei JEDEM Laden NACH dem Import (updateTx schrieb das
  // /1000-Ergebnis zurueck nach Firestore), ueberlebte also jeden Wipe+Reimport.
  // Root Cause des wiederkehrenden 18M-SHIB-/Bestandschaos. Falsche Mengen
  // korrigiert der User manuell im Bearbeiten-Formular — niemals automatisch raten.

  const handleSaveTx = async (tx, linkInfo, isExternal) => {
    // TX-Limit prüfen: nur wenn die neue TX im laufenden Steuerjahr liegt.
    const txYear = getTxYear(tx);
    if (Number.isFinite(txYearLimit) && txYear === getCurrentTaxYear()) {
      const usage = getYearUsage(txs, txYearLimit);
      if (usage.atLimit) {
        setLimitGate({ mode: 'block', usage });
        return;
      }
    }
    const fields = { ...tx, source: 'manual' };
    if (isExternal) fields.externalTransfer = true;
    if (linkInfo?.docId) {
      fields.linkedTxId = linkInfo.docId;
    }
    const newDocId = await addTx(fields);
    if (linkInfo?.docId && newDocId) {
      await patchTx(linkInfo.docId, { linkedTxId: newDocId });
    }
    showToast('✓ Transaktion gespeichert');
    popView();
  };

  const handleEditTx = (tx) => {
    // Schutz gegen Daten-Korruption beim Bearbeiten VERDICHTETER Zeilen:
    // mergeTxsByDay fasst gleichartige Trades zu einer Zeile zusammen (summierte
    // menge/preis, _docId der ERSTEN TX). Würde man diese Summen-Zeile direkt
    // speichern, bekäme die erste echte TX den Summenwert (z.B. 2×375 → 750) und
    // der Rest bliebe verwaist — genau der HBAR-Fehler. Deshalb: bei _count>1 auf
    // die echte Roh-TX (Original-Werte aus der un-verdichteten Liste) zurückmappen.
    let target = tx;
    if (tx && tx._count > 1) {
      const id = tx._docId || String(tx.id);
      const real = txs.find(t => (t._docId || String(t.id)) === id);
      if (real) target = real;
      showToast(`Diese Zeile bündelt ${tx._count} gleichartige Buchungen — du bearbeitest die erste.`);
    }
    pushView({ wizardMode: 'edit', editingTx: target });
  };

  const handleUpdateTx = async (tx) => {
    // userEdited markiert die TX als vom Nutzer autoritativ bearbeitet. Schützt
    // sie vor dem Stale-Parser-Replace-Flow (ImportReviewScreen), der bei
    // Re-Imports sonst die manuelle Klassifikation überschreibt — z.B. fielen
    // die 2×375 HBAR „an Freund" bei jedem CSV-Re-Import auf „gesendet" zurück.
    await updateTx({ ...tx, userEdited: true });
    showToast('✓ Transaktion aktualisiert');
    popView();
  };

  // Ein-Klick-Backfill fehlender Preise über die GESAMTE bestehende Historie:
  // leitet EUR-Werte ab (Swaps aus der bekannten Seite, Income/Transfers via
  // historischem Tagespreis) und persistiert sie. Behebt „Basis unvollständig" +
  // Phantom-Verluste bei schon importierten Daten — der Kunde muss nichts von Hand
  // nachtragen. Echte Preise/Kauf/Verkauf bleiben unangetastet (enrichMissingPrices).
  const handleAutoFillPrices = useCallback(async () => {
    try {
      const clones = txs.map(t => ({ ...t }));
      const n = await enrichMissingPrices(clones);
      if (!n) return 0;
      let saved = 0;
      for (const c of clones) {
        if (c._priceDerived && Number(c.preis) > 0) {
          try { await patchTx(c._docId || c.id, { preis: Number(c.preis) }); saved++; }
          catch { /* einzelner Patch-Fehler ignorieren */ }
        }
      }
      if (saved > 0) showToast(`✓ ${saved} Preise automatisch ermittelt`);
      return saved;
    } catch { return 0; }
  }, [txs, patchTx]);

  // AUTOMATISCH (keine Knöpfe): sobald die TX geladen sind, fehlende EUR-Werte einmalig
  // über die GANZE Historie ableiten + persistieren (Swaps aus der bekannten Seite,
  // Kauf/Verkauf/Income/Transfers via historischem Tagespreis). Behebt „Basis
  // unvollständig"/Phantom-Verluste ohne Zutun des Kunden. Ref-Guard → genau 1× pro
  // Mount; nachfolgende Snapshot-Updates feuern nicht erneut. Unbepreisbare Reste
  // (z.B. Memecoin↔Memecoin) bleiben offen und werden nicht endlos neu versucht.
  const autoPriceRanRef = useRef(false);
  useEffect(() => {
    if (autoPriceRanRef.current || txsLoading || !txs.length) return;
    autoPriceRanRef.current = true;
    handleAutoFillPrices();
  }, [txs, txsLoading, handleAutoFillPrices]);

  // ── Transfer-Verbinden/Trennen/Gegenbuchung — geteilt von WalletScreen (mobiler
  //    Verbinden-Modus) und der Edit-Maske (TransactionEditScreen, Web + Mobil) ──
  const handleLinkTransfer = async (outTx, inTx) => {
    const outId = outTx._docId, inId = inTx._docId;
    if (!outId || !inId) { showToast('!Verbinden fehlgeschlagen'); throw new Error('missing docId'); }
    const groupId = `mtg_${outId}`;
    try {
      await Promise.all([
        patchTx(outId, { linkedTxId: inId,  manualLink: true, transferGroupId: groupId }),
        patchTx(inId,  { linkedTxId: outId, manualLink: true, transferGroupId: groupId }),
      ]);
      showToast('Transfer verbunden');
    } catch (e) { showToast('!Verbinden fehlgeschlagen'); throw e; }
  };

  const handleUnlinkTransfer = async (txA, txB) => {
    const aId = txA._docId, bId = txB?._docId;
    if (!aId || !bId) { showToast('!Trennen fehlgeschlagen'); throw new Error('missing docId'); }
    const cleared = { linkedTxId: null, manualLink: null, transferGroupId: null };
    try {
      await Promise.all([ patchTx(aId, cleared), patchTx(bId, cleared) ]);
      showToast('Transfer getrennt');
    } catch (e) { showToast('!Trennen fehlgeschlagen'); throw e; }
  };

  // Legt die fehlende Gegenseite eines Transfers an (Auszahlung↔Einzahlung,
  // bestandsneutral bei Match) und verlinkt sie sofort. Löst den Fall, dass die
  // Gegenbuchung gar nicht (mehr) existiert — z.B. weil eine Börse das Asset
  // nicht mehr exportiert. userEdited:true schützt das Paar vor dem Replace-Wipe.
  const handleCreateCounterpart = async (sourceTx, { exchange } = {}) => {
    const srcId = sourceTx._docId;
    if (!srcId) { showToast('!Anlegen fehlgeschlagen'); throw new Error('missing docId'); }
    const isOut = sourceTx.typ === 'auszahlung';
    const counterpart = {
      typ: isOut ? 'einzahlung' : 'auszahlung',
      coin: sourceTx.coin,
      coinId: sourceTx.coinId || COIN_MAP[sourceTx.coin]?.id,
      datum: sourceTx.datum,
      zeit: sourceTx.zeit || null,
      menge: Number(sourceTx.menge) || 0,
      preis: 0,
      stueck: 0,
      gebuehr: 0,
      hw: false,
      exchange: exchange || 'Externe Wallet',
      notiz: '',
      source: 'manual',
      userEdited: true,
    };
    try {
      const newId = await addTx(counterpart);
      if (!newId) throw new Error('addTx returned null');
      const outId = isOut ? srcId : newId;
      const inId  = isOut ? newId : srcId;
      const groupId = `mtg_${outId}`;
      await Promise.all([
        patchTx(outId, { linkedTxId: inId,  manualLink: true, transferGroupId: groupId }),
        patchTx(inId,  { linkedTxId: outId, manualLink: true, transferGroupId: groupId }),
      ]);
      showToast('Gegenbuchung angelegt & verbunden');
    } catch (e) { showToast('!Anlegen fehlgeschlagen'); throw e; }
  };

  const handleImportCsv = async (rows, options = {}) => {
    // options.wipeIds: Array von TX-IDs die VOR dem Import gelöscht werden.
    // Genutzt vom ImportReviewScreen wenn stale Parser-Version-TXs ersetzt
    // werden — wir löschen die alten, danach laufen die neuen ohne Dedup-
    // Konflikt durch addBatch. Siehe simpleCsvImporter.PARSER_VERSION.
    const wipeIds = Array.isArray(options.wipeIds) ? options.wipeIds : [];
    // Rows können von 3 Quellen kommen: CSV-Import, KI-Foto-Erkennung, Exchange-API.
    // Exchange-API-rows tragen ein `externalId`-Feld ("bitpanda:trade:xxx") — daran
    // erkennen wir die Quelle und setzen source/notiz entsprechend, damit der User
    // in der App nachvollziehen kann woher eine TX kommt.
    const mapped = rows.map((r, i) => {
      const isApi = !!r.externalId;
      // Wallet-/On-Chain-Import: die Parser (Alchemy/Blockchair/Horizon/rippled/Koios)
      // liefern den Chain-Hash als `txHash`. Daran erkennen wir die Quelle — und MÜSSEN
      // den Hash erhalten: er dominiert die Dedup (txFingerprint.contentHash). Bisher
      // wurde er fallengelassen (Mapping las nur `r.hash`) → die stärkste Dedup-Stufe
      // griff bei Wallet-Importen NIE (Re-Import = Dubletten). Diese Rows sind bereits
      // on-chain → keine erneute Blockchain-Verifikation nötig (blockchainVerified=true
      // klammert sie aus dem enrich-Lauf aus).
      const isChain = !isApi && !!r.txHash;
      const source = isApi ? 'exchange_api' : (isChain ? (r.source || 'chain_import') : 'csv_import');
      const notiz = isApi ? `${r.exchange || 'Exchange'}-API` : (isChain ? `${r.exchange || 'Wallet'}-Import` : 'CSV Import');
      // Parser-Version durchreichen (gesetzt von simpleCsvImporter beim Stamping).
      // Fehlt sie (manuelle/ältere Importpfade) → bleibt undefined → ImportReviewScreen
      // behandelt sie wie pre-versioning.
      const parserVersion = r.parserVersion;

      if (r.typ === 'swap') {
        const metaOut = COIN_MAP[r.symOut] || { id: (r.symOut || '').toLowerCase() };
        const metaIn  = COIN_MAP[r.symIn]  || { id: (r.symIn  || '').toLowerCase() };
        return {
          id: Date.now() + i,
          typ: 'swap',
          datum: r.datum,
          zeit: r.zeit || null,
          coinOut: r.symOut, coinIdOut: metaOut.id, mengeOut: r.mengeOut,
          coinIn:  r.symIn,  coinIdIn:  metaIn.id,  mengeIn:  r.mengeIn,
          // On-Chain-Token-Icons (Helius/Jupiter) durchreichen — sonst gehen sie
          // beim Mapping verloren und der Coin zeigt nur den Buchstabenkreis.
          ...(r.coinIconOut ? { coinIconOut: r.coinIconOut } : {}),
          ...(r.coinIconIn ? { coinIconIn: r.coinIconIn } : {}),
          // Solana-Mint durchreichen — macht den Jupiter-Preis/Icon-Lookup
          // eindeutig (statt Symbol-Suche) und dient späterem On-Chain-Matching.
          ...(r.mintOut ? { mintOut: r.mintOut } : {}),
          ...(r.mintIn ? { mintIn: r.mintIn } : {}),
          preis: r.preis,
          gebuehr: r.gebuehr || 0,
          hash: r.hash || '',
          exchange: r.exchange,
          notiz,
          source,
          // API-spezifische Felder — nur gesetzt wenn vorhanden
          ...(r.externalId ? { externalId: r.externalId } : {}),
          ...(r.feesIncludedInSpread ? { feesIncludedInSpread: true } : {}),
          ...(r.externalTransfer ? { externalTransfer: true } : {}),
          ...(parserVersion != null ? { parserVersion } : {}),
        };
      }
      const coinMeta = COIN_MAP[r.sym] || { id: r.sym.toLowerCase(), color: '#888', bg: '#1a1a22' };
      return {
        id: Date.now() + i,
        typ: r.typ,
        coin: r.sym,
        // Wallet-Parser kennen die EXAKTE CoinGecko-ID (z.B. ONDO→ondo-finance, contract-
        // verifiziert). Die hat Vorrang vor COIN_MAP[sym]/sym.toLowerCase(), sonst landet
        // ONDO als „ondo" → kein Live-Preis. Nur für On-Chain-Rows (sonst CSV-Verhalten).
        coinId: (isChain && r.coinId) ? r.coinId : coinMeta.id,
        datum: r.datum,
        zeit: r.zeit || null,
        menge: r.menge,
        // On-Chain-Token-Icon (Helius/Jupiter) durchreichen — sonst verloren.
        ...(r.coinIcon ? { coinIcon: r.coinIcon } : {}),
        // Solana-Mint durchreichen (eindeutiger Jupiter-Preis/Icon-Lookup).
        ...(r.mint ? { mint: r.mint } : {}),
        preis: r.preis,
        stueck: r.menge > 0 ? r.preis / r.menge : 0,
        gebuehr: r.gebuehr || 0,
        hash: r.hash || r.txHash || '',
        exchange: r.exchange,
        hw: false,
        notiz,
        source,
        ...(isChain ? { blockchainVerified: true } : {}),
        ...(r.externalId ? { externalId: r.externalId } : {}),
        ...(r.feesIncludedInSpread ? { feesIncludedInSpread: true } : {}),
        // On-Chain-Gegenadressen (Wallet-Parser / Hash-Lookup) durchreichen — Basis
        // für die adressbasierte own-wallet-Erkennung (classifyOwnWalletTransfers).
        ...(r.chainFrom ? { chainFrom: r.chainFrom } : {}),
        ...(r.chainTo ? { chainTo: r.chainTo } : {}),
        // externalTransfer wird vom Parser für Withdraw/Deposit/Asset-Recovery/
        // Token-Swap-Rebranding gesetzt. Bisher ging er hier verloren — dadurch
        // erschienen BTT-Rebranding-TXs als „Binance → ?" statt „→ Externe Wallet",
        // und Withdraws ohne Gegenseite hingen als Phantom-Lots im FIFO.
        ...(r.externalTransfer ? { externalTransfer: true } : {}),
        ...(parserVersion != null ? { parserVersion } : {}),
      };
    });

    const transfersWithHash = mapped.filter(tx => tx.hash && !tx.blockchainVerified && (tx.typ === 'einzahlung' || tx.typ === 'auszahlung'));
    let enriched = mapped;
    if (transfersWithHash.length > 0) {
      showToast('Blockchain wird geprüft…');
      enriched = await enrichTransactions(mapped);
    }

    // ── Adressbasierte own-wallet-Erkennung ───────────────────────────────────
    // Transfers, deren On-Chain-Gegenadresse EXAKT einer unter „Meine Wallets"
    // hinterlegten Adresse entspricht, werden als eigener Wallet-Transfer markiert
    // (Label = Wallet-Name) und KEINE steuerpflichtige Veräußerung (externalTransfer
    // wird gelöscht → fifo lässt die Lots intakt). Nur exakter Adress-Beweis zählt;
    // ohne Registry/Treffer bleibt alles unverändert. Ein Fehler beim Laden der
    // Registry darf den Import NIE blockieren.
    // Eigene Wallet-Adressen, die der Import SELBST kennt (Ledger „Account xpub"),
    // automatisch in „Meine Wallets" registrieren — der Kunde muss nichts mehr von
    // Hand eintragen. Idempotent (bekannte Adressen werden übersprungen) und
    // fehlertolerant (darf den Import nie blockieren). Muss VOR dem Abgleich laufen.
    try {
      const ownAddrs = [];
      const seen = new Set();
      for (const r of rows) {
        const a = r?._ownAddr;
        if (a && !seen.has(a)) {
          seen.add(a);
          ownAddrs.push({ label: r._ownLabel || 'Wallet', chain: r._ownChain || 'chain', address: a, provider: r._ownProvider || null });
        }
      }
      if (ownAddrs.length && user?.uid) await ensureOwnWallets(user.uid, ownAddrs);
    } catch (err) {
      console.warn('[handleImportCsv] Auto-Wallet-Registrierung übersprungen:', err);
    }

    try {
      const myWallets = await listWallets(user?.uid);
      if (myWallets.length) enriched = classifyOwnWalletTransfers(enriched, myWallets);
    } catch (err) {
      console.warn('[handleImportCsv] Wallet-Registry-Abgleich übersprungen:', err);
    }

    // ── Auto-Preis-Anreicherung (nimmt dem Kunden das manuelle Nachtragen ab) ──
    // Füllt fehlende EUR-Werte QUELLENUNABHÄNGIG (CSV, xlsx, jede Chain-Wallet, jede
    // Börse — alles landet hier): Swaps werden aus der BEKANNTEN Seite bewertet
    // (z.B. SOL → Memecoin: Wert = SOL-Menge × historischer Tagespreis → Kostenbasis
    // für BEIDE Seiten), Income/Transfers über den historischen Tagespreis. Nutzt die
    // bestehende Anbindung getHistoricalPriceAt (CoinGecko→Binance→CryptoCompare).
    // Behebt „0,00 € getauscht / Basis unvollständig" + Phantom-Verluste. Echte Preise
    // und Kauf/Verkauf bleiben unangetastet; best-effort, blockiert den Import nie.
    try {
      const n = await enrichMissingPrices(enriched);
      if (n > 0) showToast(`${n} Preise automatisch ermittelt`);
    } catch (err) {
      console.warn('[handleImportCsv] Auto-Preis-Anreicherung übersprungen:', err);
    }

    // TX-Limit prüfen: würde der Import das Jahres-Limit überschreiten?
    const check = checkBatchAgainstLimit(txs, enriched, txYearLimit);
    if (!check.ok) {
      setLimitGate({
        mode: 'exceed',
        usage: { year: check.year, count: check.count, limit: check.limit, newInYear: check.newInYear, wouldExceedBy: check.wouldExceedBy },
      });
      return;
    }

    // Replace-Mode: stale Parser-Version-TXs zuerst löschen, damit die neuen
    // contentHashes nicht gegen die alten gebrochenen Hashes deduppen.
    let wiped = 0;
    let relinked = 0;
    if (wipeIds.length > 0) {
      const wipeSet = new Set(wipeIds.map(String));
      const byId = new Map(txs.map(t => [(t._docId || String(t.id)), t]));
      for (const id of wipeIds) {
        const stale = byId.get(String(id));
        if (stale) {
          try { await deleteTx(stale); wiped++; } catch (err) {
            console.warn('[handleImportCsv] wipe fehlgeschlagen für', id, err);
          }
        }
      }
      // Stale linkedTxId-Verweise aufräumen: TXs die NICHT gewipet wurden,
      // aber via linkedTxId auf eine gewipete TX zeigen. Sonst greift im
      // transferMatcher der manualLink-Branch ins Leere (`partner = undefined`)
      // und das Re-Pairing über Score scheitert latent (Symptom: „Ledger → ?"
      // im Wallet-Display). Wir nullen linkedTxId + manualLink + transferGroupId,
      // damit der Score-Matcher die TX frisch behandelt und mit der NEUEN
      // Binance-Gegenseite verkettet.
      for (const tx of txs) {
        const ownId = tx._docId || String(tx.id);
        if (wipeSet.has(ownId)) continue;
        if (!tx.linkedTxId) continue;
        if (!wipeSet.has(String(tx.linkedTxId))) continue;
        try {
          await patchTx(ownId, {
            linkedTxId: null,
            manualLink: null,
            transferGroupId: null,
          });
          relinked++;
        } catch (err) {
          console.warn('[handleImportCsv] linkedTxId-Cleanup fehlgeschlagen für', ownId, err);
        }
      }
    }

    // Dedup übernimmt jetzt addBatch via contentHash + Blockchain-Hash —
    // KEINE manuelle Vorfilterung mehr (alte Logik kollabierte 18:30:00 +
    // 18:30:20 zu einer einzigen TX).
    // excludeIds: gerade gewipete TXs aus dem Dedup-Set ausklammern. Sonst
    // greift der Race-Bug: deleteTx hat Firestore aktualisiert, aber React-
    // State `txs` ist noch stale → addBatch würde die neuen TXs gegen die
    // toten Alt-Hashes matchen und alle 173 als Duplikate verwerfen.
    const { added, dupes, scrubbed = 0 } = await addBatch(enriched, {
      excludeIds: wipeIds.length > 0 ? wipeIds : undefined,
    });

    if (added === 0 && wiped === 0 && scrubbed === 0) {
      showToast('Alle Transaktionen bereits vorhanden – nichts importiert');
      exitImportFlow();
      return;
    }
    const verified = enriched.filter(tx => tx.blockchainVerified).length;
    const verifiedNote = verified > 0 ? ` · ${verified} on-chain verifiziert` : '';
    const wipedNote = wiped > 0 ? ` · ${wiped} alte ersetzt` : '';
    const relinkedNote = relinked > 0 ? ` · ${relinked} Verknüpfungen neu aufgebaut` : '';
    // scrubbed = Wallet-Eingänge, die einen MoonPay-Kauf oder Swap-Output doppelten.
    const scrubbedNote = scrubbed > 0 ? ` · ${scrubbed} Wallet-Dubletten entfernt` : '';
    showToast(`✓ ${added} importiert${dupes > 0 ? ` · ${dupes} Duplikate übersprungen` : ''}${scrubbedNote}${wipedNote}${relinkedNote}${verifiedNote}`);
    exitImportFlow();
  };

  useEffect(() => {
    if (txsLoading || !txs.length) return;
    const pairs = matchTransfers(normalizeTransactions(txs));
    if (pairs.length) persistTransferLinks(pairs, txs, patchTx);
  }, [txs, txsLoading, patchTx]);

  // #3 — Eindeutige Eigen-Transfer-Kandidaten der Wallet-Überwachung beim Start
  // automatisch übernehmen (app-weit, nicht erst beim Öffnen von „Meine Wallets").
  // Einmal pro Session, sobald eingeloggt + Onboarding fertig. Sicher: nur
  // Adress-Treffer in „Meine Wallets" (INVARIANT 4), Import über addBatch; Rest
  // bleibt im „Zu prüfen". Best-effort — Fehler erlauben einen erneuten Versuch.
  const autoImportRan = useRef(false);
  useEffect(() => {
    if (autoImportRan.current) return;
    if (!user?.uid || !onboardingComplete) return;
    autoImportRan.current = true;
    autoImportOwnTransferCandidates({ uid: user.uid, onImport: handleImportCsv })
      .then(n => { if (n > 0) showToast(t('wr_auto_imported', { n })); })
      .catch(() => { autoImportRan.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, onboardingComplete]);

  const scrollRef = useRef(null);
  // Brücke: IonicShell setzt hier seine react-router-history, damit Overlays
  // (Add-Wizard) ausserhalb des Routers Ionic-Routen ansteuern können.
  const ionNavRef = useRef(null);

  // ── Status-Bar-Scrim (WhatsApp-Stil) ──────────────────────────────────────
  // Beim Scrollen blendet oben ein theme-passender Verlauf + Blur ein (nur die
  // Safe-Area-Zone), damit die iOS-Uhrzeit/-Datum über dem durchscrollenden
  // Inhalt sauber lesbar bleibt. Folgt T.bg → wirkt in Light wie Dark.
  const [topScrim, setTopScrim] = useState(0);
  const handleShellScroll = (e) => setTopScrim(Math.min(1, e.target.scrollTop / 36));

  // Scroll-Reset bei JEDEM Screen-/Kategorie-Wechsel.
  // Der Scroll-Container (mobile: scrollRef-div; desktop: <main> in DesktopShell)
  // bleibt beim Tab-Wechsel gemountet — nur die children tauschen. Ohne Reset
  // erbt der neue Screen die Scroll-Position des vorherigen ("Märkte runtergescrollt
  // → Transaktionen ebenfalls runtergescrollt"). navSignature ändert sich bei jedem
  // Wechsel von Tab, Markets-/Alerts-/Pricing-Route, Wizard, Import oder Asset-Detail.
  const navSignature = [
    tab,
    marketsOpen ? 'mk' : '',
    wizardMode || '',
    editingTx ? 'edit' : '',
    selectedAsset ? (selectedAsset.coin || selectedAsset.id || 'asset') : '',
  ].join('|');
  // useLayoutEffect: vor dem Paint zurücksetzen → kein sichtbares Springen.
  // Greift nur mobil (scrollRef-div); Desktop reset läuft in DesktopShell via scrollKey.
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [navSignature]);

  if (authLoading) return <SplashScreen />;
  if (!user) return <LoginScreen />;
  if (!onboardingComplete) return <OnboardingScreen user={user} onConnectSource={(m) => pushView({ wizardMode: m })} />;

  const handleTabChange = (t) => {
    if (t === 'add') {
      pushView({ wizardMode: 'choose' });
      return;
    }
    // Editorial v4: zusätzliche Sidebar-Routen mappen auf bestehende Sub-Views.
    if (t === 'markets') {
      setViewStack([]); setSelectedAsset(null); setWizardMode(null); setEditingTx(null);
      setMarketsOpen(true);
      return;
    }
    if (t === 'alerts') {
      setViewStack([]); setSelectedAsset(null); setMarketsOpen(false); setEditingTx(null);
      setWizardMode('alerts');
      return;
    }
    if (t === 'pricing') {
      // Wird als vollwertiger Tab gerendert (kein Overlay mehr für Sidebar-Nav).
      // Overlay-Modus bleibt für setPricingOpen() aus LimitGateModal etc.
      setViewStack([]); setSelectedAsset(null); setMarketsOpen(false); setEditingTx(null);
      setWizardMode(null);
      setTab('pricing');
      return;
    }
    // Top-level Tab-Wechsel: Stack zurücksetzen, Sub-Views schließen.
    setViewStack([]);
    setSelectedAsset(null);
    setMarketsOpen(false);
    setWizardMode(null);
    setEditingTx(null);
    // Wallet-Filter beim Verlassen des Tabs zurücksetzen
    if (t !== 'wallet') setWalletView({ filterUnmatched: false, activeTab: 'all', filterText: '' });
    setTab(t);
    // Scroll-Reset passiert zentral via navSignature-useLayoutEffect (deckt auch
    // die früh-return-Routen Markets/Alerts/Pricing und Asset-Detail mit ab).
  };

  // Aktiv-Marker für die Sidebar: berücksichtigt Sub-Views.
  const activeNav = wizardMode === 'alerts' ? 'alerts'
    : marketsOpen ? 'markets'
    : tab;

  // TopBar-Back: nur sichtbar, wenn wir auf einer Sub-View sind.
  // Sheet-Wizards (choose/manual) brauchen keinen TopBar-Back — Sheet hat eigenen Close.
  const sheetWizard = wizardMode === 'choose' || wizardMode === 'manual' || wizardMode === 'datacheckFix';
  const isOnSubView = !!((wizardMode && !sheetWizard) || selectedAsset || marketsOpen);
  const onBack = isOnSubView ? popView : privacyOpen ? () => setPrivacyOpen(false) : null;
  const backTitle = selectedAsset
    ? (selectedAsset.name || selectedAsset.sym)
    : marketsOpen ? t('markets')
    : wizardMode === 'alerts' ? t('alerts')
    : wizardMode === 'csv' ? t('import')
    : wizardMode === 'api' ? 'Exchange'
    : wizardMode === 'wallet' ? 'Solana'
    : wizardMode === 'utxowallet' ? t('tw_method_utxo_title')
    : wizardMode === 'wallets' ? t('wr_title')
    : wizardMode === 'datacheckFix' ? t('dc_fix_title')
    : wizardMode === 'edit' ? t('asset_edit')
    : privacyOpen ? t('settings')
    : null;
  const onOpenAlerts = () => pushView({ wizardMode: 'alerts', selectedAsset: null, marketsOpen: false });

  const screenContent = (
    <>
      {wizardMode === 'edit' ? (
          <TransactionEditScreen
            tx={editingTx}
            txs={txs}
            marketData={marketData}
            onSave={handleUpdateTx}
            onBack={popView}
            onLink={async (o, i) => { await handleLinkTransfer(o, i); popView(); }}
            onUnlink={async (a, b) => { await handleUnlinkTransfer(a, b); popView(); }}
            onCreateCounterpart={async (s, opts) => { await handleCreateCounterpart(s, opts); popView(); }}
          />
        ) : selectedAsset ? (
          <AssetDetailScreen
            asset={selectedAsset}
            onBack={popView}
            txs={txs.filter(t =>
              t.coin === selectedAsset.sym || t.sym === selectedAsset.sym ||
              t.coinOut === selectedAsset.sym ||
              t.coinIn === selectedAsset.sym
            )}
            marketData={marketData}
            onEdit={handleEditTx}
            onDelete={async (id) => {
              const tx = txs.find(t => t.id === id || String(t.id) === String(id));
              if (tx) await deleteTx(tx);
              showToast('Transaktion gelöscht');
            }}
            onOpenPricing={() => setPricingOpen(true)}
            onOpenAlert={(asset) => pushView({ wizardMode: 'alerts', alertPrefill: asset, selectedAsset: null })}
          />
        ) : wizardMode === 'csv' ? (
          <SettingsScreen mode="csv" onImport={handleImportCsv} onBack={popView} uid={user?.uid} existingTxs={txs} />
        ) : wizardMode === 'api' ? (
          <SettingsScreen
            mode="api"
            onBack={popView}
            onImport={handleImportCsv}
            onOpenPricing={() => setPricingOpen(true)}
          />
        ) : wizardMode === 'wallet' ? (
          <SolanaWalletConnect onBack={popView} onImport={handleImportCsv} txs={txs} patchTx={patchTx} deleteTx={deleteTx} showToast={showToast} />
        ) : wizardMode === 'utxowallet' ? (
          <ChainWalletConnect onBack={popView} onImport={handleImportCsv} txs={txs} uid={user?.uid} showToast={showToast} />
        ) : wizardMode === 'wallets' ? (
          <WalletRegistry uid={user?.uid} marketData={marketData} onBack={popView} onImport={handleImportCsv} txs={txs} showToast={showToast} />
        ) : wizardMode === 'datacheckFix' ? (
          <DataCheckFixScreen txs={txs} marketData={marketData} onBack={popView} onEdit={handleEditTx} />
        ) : wizardMode === 'alerts' ? (
          <AlertsScreen
            onBack={popView}
            onOpenPricing={() => setPricingOpen(true)}
            txs={txs}
            marketData={marketData}
            prefill={alertPrefill}
            onPrefillConsumed={() => setAlertPrefill(null)}
          />
        ) : marketsOpen ? (
          // Moderner Einblend-Effekt (slide-up + scale + fade). CSS statt framer,
          // damit nach der Animation KEIN Transform bleibt → sticky-Tabellenheader
          // im Markets-Screen bleibt funktionsfähig.
          <div className="screen-reveal" key="markets-reveal">
            <MarketsScreen
              marketData={marketData}
              onSelectAsset={(asset) => pushView({ selectedAsset: asset, marketsOpen: false })}
            />
          </div>
        ) : tab === 'dashboard' ? (
          <Dashboard
            txs={txs}
            marketData={marketData}
            onAddTx={() => pushView({ wizardMode: 'choose' })}
            onTabChange={handleTabChange}
            onSelectAsset={(asset) => pushView({ selectedAsset: asset })}
            onOpenMarkets={() => pushView({ marketsOpen: true })}
            onOpenAlerts={onOpenAlerts}
            onOpenDataCheckFix={() => pushView({ wizardMode: 'datacheckFix' })}
            onRefreshMarket={loadMarket}
          />
        ) : tab === 'wallet' ? (
          <WalletScreen txs={txs} marketData={marketData}
            onBack={() => handleTabChange('dashboard')}
            onAddTx={() => pushView({ wizardMode: 'choose' })}
            onOpenResolve={() => pushView({ wizardMode: 'datacheckFix' })}
            onEdit={handleEditTx}
            onDelete={async (id) => {
              const tx = txs.find(t => t.id === id || String(t.id) === String(id));
              if (tx) await deleteTx(tx);
              showToast('Transaktion gelöscht');
            }}
            onDeleteBulk={async (txsToDelete) => {
              await deleteBulkTxs(txsToDelete);
              showToast(`✓ ${txsToDelete.length} Transaktion${txsToDelete.length !== 1 ? 'en' : ''} gelöscht`);
            }}
            onLinkTransfer={handleLinkTransfer}
            onUnlinkTransfer={handleUnlinkTransfer}
            patchTx={patchTx}
            viewState={walletView}
            onViewStateChange={(partial) => setWalletView(v => ({ ...v, ...partial }))}
          />
        ) : tab === 'pricing' ? (
          <PricingScreen />
        ) : tab === 'berichte' ? (
          <HistorieScreen view="smartcheck" txs={txs} marketData={marketData} onOpenPricing={() => setPricingOpen(true)} onSelectAsset={(asset) => pushView({ selectedAsset: asset })} onOpenAlert={(prefill) => pushView({ wizardMode: 'alerts', alertPrefill: prefill, selectedAsset: null })} />
        ) : tab === 'reports' ? (
          <HistorieScreen view="reports" txs={txs} marketData={marketData} onOpenPricing={() => setPricingOpen(true)} onAutoFillPrices={handleAutoFillPrices} />
        ) : tab === 'settings' ? (
          privacyOpen ? (
            <PrivacyNotice uid={user?.uid} />
          ) : (
            <ProfileScreen
              asTab
              onClose={() => {}}
              onResetData={async () => {
                await deleteAllTxs();
                showToast('Alle Daten gelöscht');
              }}
              txCount={txs.length}
              txs={txs}
              user={user}
              onOpenPrivacy={() => setPrivacyOpen(true)}
              onOpenPricing={() => setPricingOpen(true)}
              onOpenApi={() => pushView({ wizardMode: 'api' })}
              onOpenWallets={() => pushView({ wizardMode: 'wallets' })}
              onOpenAlerts={() => pushView({ wizardMode: 'alerts', selectedAsset: null, marketsOpen: false })}
              onOpenSupport={() => setSupportOpen(true)}
            />
          )
        ) : null}
    </>
  );

  // Geteilte Overlays — auf Mobile und Desktop identisch
  const overlays = (
    <>
      {/* Manuell-Wizard als Bottom-Sheet über dem aktuellen Screen */}
      <WizardSheet
        open={wizardMode === 'choose' || wizardMode === 'manual'}
        onClose={popView}
        title="Transaktion hinzufügen"
        step={wizardStep.step}
        totalSteps={wizardStep.total}
        onBack={wizardGoBack}
      >
        {(wizardMode === 'choose' || wizardMode === 'manual') && (
          <TransactionWizard
            marketData={marketData}
            existingTxs={txs}
            onSave={handleSaveTx}
            initialMethod={wizardMode === 'choose' ? null : wizardMode}
            onStepChange={(step, total, goBack) => {
              setWizardStep({ step, total });
              // goBack als Funktion in State setzen — useState(fn) würde fn aufrufen, daher Wrapper
              setWizardGoBack(() => goBack);
            }}
            onMethodSelect={(m) => {
              // 'manual' bleibt im selben Sheet (interner, stufenweiser Zurück-Button).
              // csv/api/wallet sind eigene Sub-Views: per pushView als Stack-Eintrag öffnen,
              // damit JEDER Zurück-Button wieder an den Anfang des Erfassen-Menüs (choose) führt
              // — nicht zur Voransicht (Dashboard) wie bei direktem setWizardMode.
              if (m === 'manual') { setWizardMode('manual'); return; }
              // Ionic-Standard: Wizard-Sheet schließen + per Brücke auf die
              // Ionic-Route der Methode navigieren (swipe-back inklusive).
              if (!FORCE_OLD_NAV && ionNavRef.current) {
                const route = { csv: '/app/import/csv', api: '/app/import/api', wallet: '/app/connect/solana', utxowallet: '/app/connect/chain' }[m];
                setWizardMode(null);
                if (route) ionNavRef.current.push(route);
                return;
              }
              pushView({ wizardMode: m });
            }}
          />
        )}
      </WizardSheet>

      {/* Einheitlicher Notification-Stack oben rechts unter der Kopfleiste.
          pointerEvents:none am Container, damit der leere Bereich keine Klicks
          abfängt; die Cards re-aktivieren pointerEvents selbst. */}
      <div style={{
        position: 'fixed', top: 86, right: 20, zIndex: 360,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10,
        pointerEvents: 'none',
      }}>
        {updateAvailable && (
          <div style={{ pointerEvents: 'auto' }}>
            <UpdateBanner show={updateAvailable} onReload={() => window.location.reload()} />
          </div>
        )}
        {toast && (
          <div style={{ pointerEvents: 'auto' }}>
            <Toast message={toast} />
          </div>
        )}
      </div>

      <LimitGateModal
        open={!!limitGate}
        mode={limitGate?.mode}
        usage={limitGate?.usage}
        currentTier={tier}
        onClose={() => setLimitGate(null)}
        onUpgrade={() => { setLimitGate(null); setPricingOpen(true); }}
      />

      {pricingOpen && (
        <PricingScreen
          onClose={() => setPricingOpen(false)}
        />
      )}

      {!tutorialSeen && (
        <TourOverlay
          onNavigate={handleTabChange}
          onDone={() => { handleTabChange('dashboard'); setTutorialSeen(true); }}
          isWide={isWide}
        />
      )}

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} txs={txs} tier={tier} />
    </>
  );

  // MarketData via Context bereitstellen — Screens können via useMarketData()
  // konsumieren, ohne dass marketData durchgepropt werden muss.
  const marketCtx = { marketData, refresh: loadMarket, loading: false };

  // ── Erstkontakt: „Verbinde deine erste Quelle" ──────────────────────
  // Nach dem Onboarding, solange der Nutzer 0 Transaktionen hat UND die Stufe
  // noch nicht gesehen/übersprungen wurde. Eine Methode tippen → direkt in den
  // echten Flow (api/utxowallet/csv/manual); „Überspringen" setzt nur das Flag.
  // Vor dem isWide-Split, damit es für Desktop UND Mobile greift. Bestehende
  // Nutzer (txs > 0) sehen es nie.
  if (!firstSourceSeen && txs.length === 0 && !txsLoading) {
    return (
      <ConnectFirstSource
        user={user}
        onSelect={(m) => { setFirstSourceSeen(true); pushView({ wizardMode: m }); }}
        onSkip={() => setFirstSourceSeen(true)}
      />
    );
  }

  // ── Desktop / Tablet ───────────────────────────────────────────────
  if (isWide) {
    return (
      <MarketDataContext.Provider value={marketCtx}>
        <DesktopShell
          tab={activeNav}
          scrollKey={navSignature}
          onTabChange={handleTabChange}
          onBack={(selectedAsset || marketsOpen || (wizardMode === 'alerts' && viewStack.length === 0)) ? null : onBack}
          backTitle={backTitle}
          onOpenAlerts={onOpenAlerts}
          onOpenSupport={() => setSupportOpen(true)}
          txs={txs}
          marketData={marketData}
          onSelectAsset={(asset) => pushView({ selectedAsset: asset })}
        >
          <DeferredScreen>{screenContent}</DeferredScreen>
        </DesktopShell>
        {overlays}
      </MarketDataContext.Provider>
    );
  }

  // ── Mobile: Ionic-Shell (STANDARD — native Navigation + swipe-back) ──
  if (!FORCE_OLD_NAV) {
    return (
      <MarketDataContext.Provider value={marketCtx}>
        <Suspense fallback={<SplashScreen />}>
          <IonicShell
            navRef={ionNavRef}
            data={{ txs, marketData, user, walletView, editingTx }}
            actions={{
              onAddTx: () => pushView({ wizardMode: 'choose' }),
              onRefreshMarket: loadMarket,
              setEditingTx,
              onDelete: async (id) => { const tx = txs.find(t => t.id === id || String(t.id) === String(id)); if (tx) await deleteTx(tx); showToast('Transaktion gelöscht'); },
              onDeleteBulk: async (list) => { await deleteBulkTxs(list); showToast(`✓ ${list.length} gelöscht`); },
              onLinkTransfer: handleLinkTransfer,
              onUnlinkTransfer: handleUnlinkTransfer,
              onCreateCounterpart: handleCreateCounterpart,
              onUpdateTx: handleUpdateTx,
              patchTx,
              deleteTx,
              showToast,
              onAutoFillPrices: handleAutoFillPrices,
              onImport: handleImportCsv,
              onViewStateChange: (partial) => setWalletView(v => ({ ...v, ...partial })),
              onOpenPricing: () => setPricingOpen(true),
              onResetData: async () => { await deleteAllTxs(); showToast('Alle Daten gelöscht'); },
              onOpenSupport: () => setSupportOpen(true),
            }}
          />
        </Suspense>
        {overlays}
      </MarketDataContext.Provider>
    );
  }

  // ── Mobile (alter Shell — Notausgang ?oldnav=1) ─────────────────────
  return (
    <MarketDataContext.Provider value={marketCtx}>
      <SwipeNav
        onSwipeRight={() => {
          // Wisch nach rechts = „aus dem Menü raus". Detail-Menüs (Wallets,
          // Alarme, Datencheck, Asset-Detail, Märkte, Privacy) per Wisch
          // schließen. NUR den mehrstufigen Add-Transaktion-Flow + Edit
          // ausnehmen (eigene Schritt-Gesten, sonst Import-Fortschritt weg).
          const isAddFlow = ['choose', 'csv', 'api', 'edit'].includes(wizardMode) || editingTx;
          if (isAddFlow) return;
          if (onBack) onBack();
          else if (tab === 'dashboard') pushView({ marketsOpen: true });
        }}
        style={{
          minHeight: '100vh',
          background: T.bg,
          color: T.text,
          fontFamily: F.family, maxWidth: 480, margin: '0 auto',
          position: 'relative',
        }}
      >
        {/* WhatsApp-Stil-Scrim hinter der Status-Bar — blendet beim Scrollen ein,
           hält Uhrzeit/Datum über durchscrollendem Inhalt lesbar (Light + Dark). */}
        <div aria-hidden style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          background: `linear-gradient(to bottom, ${T.bg} 58%, transparent)`,
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          opacity: topScrim,
          transition: 'opacity .18s ease',
          pointerEvents: 'none',
          zIndex: 50,
        }} />

        <TopBar onBack={marketsOpen ? null : onBack} backTitle={marketsOpen ? null : backTitle} onOpenAlerts={onOpenAlerts} onOpenMarkets={() => handleTabChange('markets')} bare hideRight />

        <PullToRefresh
          ref={scrollRef}
          className="hide-scrollbar"
          enabled={tab === 'dashboard' && !wizardMode && !selectedAsset && !editingTx && !marketsOpen}
          onRefresh={handlePullRefresh}
          onScroll={handleShellScroll}
          style={{ overflowY: 'auto', height: '100vh', WebkitOverflowScrolling: 'touch', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)', paddingBottom: 110 }}
        >
          <DeferredScreen>{screenContent}</DeferredScreen>
        </PullToRefresh>

        {!(wizardMode === 'csv' || wizardMode === 'api' || wizardMode === 'edit') && !selectedAsset && !marketsOpen && (
          <BottomNav active={tab} onChange={handleTabChange} />
        )}

        {overlays}
      </SwipeNav>
    </MarketDataContext.Provider>
  );
}

// ── TUTORIAL OVERLAY ──────────────────────────────────────────────────────────
// Ersetzt durch den mehrstufigen Rundgang in src/components/TourOverlay.jsx.
